import { ref, onUnmounted, computed } from 'vue';
import { useAuthStore } from '../stores/auth';

interface LogLine {
  buildId: string;
  timestamp: string;
  stream: 'stdout' | 'stderr';
  line: string;
}

interface WebSocketMessage {
  type: 'logs' | 'status' | 'error' | 'subscribed' | 'unsubscribed' | 'history_start' | 'history_end' | 'pong';
  payload?: unknown;
}

type StreamingState = 'disconnected' | 'connecting' | 'replaying_history' | 'streaming' | 'finished';

export function useWebSocket(buildId: string) {
  const authStore = useAuthStore();
  const connected = ref(false);
  const logs = ref<LogLine[]>([]);
  const status = ref<string | null>(null);
  const error = ref<string | null>(null);
  const streamingState = ref<StreamingState>('disconnected');
  const historySource = ref<'storage' | 'buffer' | null>(null);

  let ws: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let pingTimer: ReturnType<typeof setInterval> | null = null;

  // Computed states for UI
  const isReplayingHistory = computed(() => streamingState.value === 'replaying_history');
  const isStreaming = computed(() => streamingState.value === 'streaming');
  const isFinished = computed(() => streamingState.value === 'finished');

  function connect() {
    // Prevent double connection
    if (ws || streamingState.value === 'connecting') {
      return;
    }

    if (!authStore.isAuthenticated) {
      error.value = 'Authentication required';
      return;
    }

    streamingState.value = 'connecting';

    // Determine WebSocket URL
    // Session cookie is sent automatically with WebSocket upgrade request
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const url = `${protocol}//${host}/api/builds/${buildId}/logs`;

    ws = new WebSocket(url);

    ws.onopen = () => {
      connected.value = true;
      error.value = null;

      // Start ping interval
      pingTimer = setInterval(() => {
        if (ws?.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);

        switch (message.type) {
          case 'logs': {
            const payload = message.payload as { logs: LogLine[] };
            logs.value.push(...payload.logs);
            break;
          }
          case 'status': {
            const payload = message.payload as { status: string };
            status.value = payload.status;
            break;
          }
          case 'error': {
            const payload = message.payload as { error: string };
            error.value = payload.error;
            break;
          }
          case 'subscribed':
            // Successfully subscribed, waiting for history or live logs
            break;
          case 'history_start': {
            // Starting to receive historical logs
            const payload = message.payload as { source: 'storage' | 'buffer' };
            streamingState.value = 'replaying_history';
            historySource.value = payload.source;
            break;
          }
          case 'history_end':
            // Finished receiving historical logs, now streaming live
            streamingState.value = 'streaming';
            historySource.value = null;
            break;
          case 'pong':
            // Server responded to ping
            break;
        }
      } catch {
        // Ignore invalid messages
      }
    };

    ws.onclose = (event) => {
      connected.value = false;
      if (pingTimer) {
        clearInterval(pingTimer);
        pingTimer = null;
      }

      // Check if this was a normal close (build finished)
      // Code 1000 = normal closure, typically sent when build is finished
      if (event.code === 1000 && event.reason === 'Build finished') {
        streamingState.value = 'finished';
      } else if (streamingState.value !== 'finished') {
        streamingState.value = 'disconnected';
      }
    };

    ws.onerror = () => {
      error.value = 'WebSocket connection error';
      connected.value = false;
      streamingState.value = 'disconnected';
    };
  }

  function disconnect() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (pingTimer) {
      clearInterval(pingTimer);
      pingTimer = null;
    }
    if (ws) {
      ws.close();
      ws = null;
    }
    connected.value = false;
    if (streamingState.value !== 'finished') {
      streamingState.value = 'disconnected';
    }
  }

  function clearLogs() {
    logs.value = [];
  }

  function reset() {
    clearLogs();
    streamingState.value = 'disconnected';
    historySource.value = null;
    status.value = null;
    error.value = null;
  }

  // Cleanup on unmount
  onUnmounted(() => {
    disconnect();
  });

  return {
    connected,
    logs,
    status,
    error,
    streamingState,
    historySource,
    isReplayingHistory,
    isStreaming,
    isFinished,
    connect,
    disconnect,
    clearLogs,
    reset,
  };
}
